import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebaseConfig';
import PropTypes from 'prop-types';

/**
 * Component that protects routes by checking for archive account state
 * Redirects archived users to the archive page
 * Redirects unauthenticated users to the login page
 * NON-BLOCKING: Renders children immediately while checking status in background
 */
export default function ArchiveProtectedRoute({ children }) {
  const [user, setUser] = useState(null);
  const [isArchive, setIsArchive] = useState(false);
  const [checksComplete, setChecksComplete] = useState(false);
  const navigate = useNavigate();
  
  useEffect(() => {
    let previousUid = null;
    
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        previousUid = currentUser.uid;
        
        // Check for cached archive status first
        const cacheKey = `archive_status_${currentUser.uid}`;
        const cachedData = sessionStorage.getItem(cacheKey);
        
        if (cachedData) {
          try {
            const { isArchive: cachedArchive, timestamp } = JSON.parse(cachedData);
            // Cache is valid for 2 minutes
            if (Date.now() - timestamp < 2 * 60 * 1000) {
              setIsArchive(cachedArchive);
              setChecksComplete(true);
              
              // Handle redirect if archived
              if (cachedArchive && window.location.pathname !== '/archive') {
                navigate('/archive');
                return;
              }
              return;
            }
          } catch (error) {
            console.error('Error parsing cached archive status:', error);
          }
        }
        
        // Check archive status in background
        try {
          const response = await fetch('/api/checkArchiveStatus', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ uid: currentUser.uid }),
          });

          if (response.ok) {
            const { isArchive: archiveStatus } = await response.json();
            setIsArchive(archiveStatus);
            
            // Cache the result
            sessionStorage.setItem(cacheKey, JSON.stringify({
              isArchive: archiveStatus,
              timestamp: Date.now()
            }));
            
            // Handle redirect if archived
            if (archiveStatus && window.location.pathname !== '/archive') {
              navigate('/archive');
              return;
            }
          }
        } catch (error) {
          console.error('Error checking archive status:', error);
        }
        setChecksComplete(true);
      } else {
        // User logged out while we were checking
        if (previousUid) {
          // Clear any cached data for the previous user
          const cacheKey = `archive_status_${previousUid}`;
          sessionStorage.removeItem(cacheKey);
        }
        setUser(null);
        setIsArchive(false);
        setChecksComplete(true);
      }
    });

    return () => unsubscribe();
  }, [navigate]);
  
  // Not logged in and checks complete - redirect to sign in
  if (checksComplete && !user) {
    window.location.href = '/';
    return null;
  }
  
  // User is archived and checks complete - redirect should have already happened
  // But if for some reason we're still here and the user is archived, redirect again
  if (checksComplete && isArchive && window.location.pathname !== '/archive') {
    navigate('/archive');
    return null;
  }
  
  // Render children immediately (non-blocking)
  return children;
} 

ArchiveProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired
}; 